import { FC, useMemo, useState } from "react";
import styled from "styled-components";
import { useInfiniteQuery } from "@tanstack/react-query";

import SearchBar from "@/components/SearchBar";
import useDebounce from "@/hooks/useDebounce";
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  Th,
} from "@/components/Table";
import { SectionHeading, SrOnly } from "@/components/Layout/Parts";
import InfiniteLoader from "@/components/InfiniteLoader";
import {
  PaginatedResponse,
  ReportsGroupResource,
  Role,
} from "@/types/resources";
import { api } from "@/utils/api";
import GroupRow from "./GroupRow";
import useSort from "@/hooks/useSort";
import { SortDirection } from "@/utils/enums";

const StyledTable = styled(Table)`
  th,
  td {
    padding: 16px;
    font-size: 11px;
    vertical-align: middle;

    &:nth-child(1) {
      width: 32%;
      padding-left: 24px;
      padding-right: 56px;
    }
    &:nth-child(2) {
      width: 17%;
    }
    &:nth-child(3) {
      width: 13%;
    }
  }
`;

interface Props {
  userRole: Role;
}

const GroupsTable: FC<Props> = ({ userRole }) => {
  const [search, setSearch] = useState("");

  const debouncedSearch = useDebounce(search, 400);

  const [sort, setSort] = useSort({
    property: "created_at",
    direction: SortDirection.DEFAULT,
  });

  const fetchReports = ({ pageParam = `/v1/reports-groups` }) => {
    return api.get<never, PaginatedResponse<ReportsGroupResource>>(pageParam, {
      params: {
        ...(!!debouncedSearch && {
          "filter[search]": debouncedSearch,
        }),
        ...(sort.direction !== SortDirection.DEFAULT && {
          sort: sort.direction === SortDirection.ASC ? "" : "-" + sort.property,
        }),
      },
    });
  };

  const {
    data,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery<PaginatedResponse<ReportsGroupResource>>(
    [
      "reports_groups",
      {
        ...(!!debouncedSearch && { search: debouncedSearch }),
        sort,
      },
    ],
    fetchReports,
    {
      getNextPageParam: (lastPage) => {
        return lastPage.links.next;
      },
      staleTime: 60 * 1000,
    }
  );

  const groups = useMemo(
    () =>
      Object.keys(data?.pages || {}).reduce<ReportsGroupResource[]>(
        (acc, currKey: any) => {
          return [...acc, ...data!.pages[currKey].data];
        },
        []
      ),
    [data]
  );

  return (
    <>
      <SearchBar value={search} setValue={setSearch} />
      <SectionHeading>Groups list</SectionHeading>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>Group Name</Th>
              <SortableTh
                property="created_at"
                title="Date Created"
                sort={sort}
                setSort={setSort}
              />
              <Th>Quantity</Th>
              <Th>Note</Th>
              <Th $actionsCol>
                <SrOnly>Actions</SrOnly>
              </Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              isLoading={isLoading}
              colSpan={5}
              isError={isError}
              hasData={!!groups?.length}
            >
              {groups?.map((group) => (
                <GroupRow key={group.id} group={group} userRole={userRole} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
      <InfiniteLoader
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
      />
    </>
  );
};

export default GroupsTable;
